/**
 * Viennin sarakevalinta (suunnitelma 11).
 *
 * CSV menee laskutukseen, ja laskutusaineistossa on kaksi vastakkaista
 * tarvetta: rivin pitää olla tunnistettavissa, muttei kertoa enempää kuin
 * on tarpeen. Kirjauksen kuvaus on tästä se selkein tapaus — se on
 * kirjoitettu sisäiseksi muistiinpanoksi, ei asiakkaan luettavaksi.
 *
 * Siksi **kuvaus ja attribuutit ovat oletuksena pois** ja käyttäjä valitsee
 * ne mukaan tarvittaessa. Oletus on se, joka päätyy tiedostoon silloin kun
 * kukaan ei ajattele asiaa, ja tässä turvallisempi oletus on suppeampi.
 *
 * Sarakkeen tunniste on sama merkkijono kuin CSV:n otsikko. Se pitää
 * osoitteen luettavana (`?columns=pvm,asiakas_koodi,tunnit`) eikä anna
 * kahta nimeä samalle asialle. Attribuuttisarake on `attr_`-etuliitteinen,
 * jottei ylläpitäjän perustama avain voi törmätä kiinteään sarakkeeseen.
 *
 * Valinta koskee **vain CSV:tä**. JSON on koneluettava kokonaisesitys, ja
 * sen karsiminen tekisi kahdesta viennistä kaksi eri aineistoa.
 */
/** Attribuuttisarakkeen etuliite CSV:ssä ja `columns`-parametrissa. */
export const ATTRIBUTE_COLUMN_PREFIX = 'attr_';
/**
 * Kiinteät sarakkeet kanonisessa järjestyksessään.
 *
 * Järjestys ei tule käyttäjän valinnasta: valinta kertoo *mitkä*
 * sarakkeet, ei missä järjestyksessä. Kahdella samoilla sarakkeilla
 * ajetulla viennillä on siis aina sama muoto.
 */
export const EXPORT_COLUMNS = [
    { id: 'pvm', default: true },
    { id: 'viikko', default: true },
    { id: 'kayttaja', default: true },
    { id: 'kayttaja_email', default: true },
    { id: 'asiakas_koodi', default: true },
    { id: 'asiakas_nimi', default: true },
    { id: 'tehtavalaji_koodi', default: true },
    { id: 'tehtavalaji_nimi', default: true },
    { id: 'projekti_koodi', default: true },
    { id: 'projekti_nimi', default: true },
    { id: 'tunnit', default: true },
    { id: 'laskutettava', default: true },
    { id: 'tuntihinta', default: true },
    { id: 'arvo', default: true },
    { id: 'valuutta', default: true },
    // Sisäinen muistiinpano. Pois oletuksesta — ks. tiedoston alku.
    { id: 'kuvaus', default: false },
    { id: 'kirjaus_id', default: true },
    { id: 'luotu', default: true },
    { id: 'muokattu', default: true },
];
const COLUMN_IDS = new Set(EXPORT_COLUMNS.map((column) => column.id));
export const DEFAULT_EXPORT_COLUMNS = EXPORT_COLUMNS.filter((column) => column.default).map((column) => column.id);
export function isExportColumnId(value) {
    return COLUMN_IDS.has(value);
}
export function attributeColumnId(key) {
    return `${ATTRIBUTE_COLUMN_PREFIX}${key}`;
}
export function attributeKeyOfColumn(id) {
    return id.startsWith(ATTRIBUTE_COLUMN_PREFIX)
        ? id.slice(ATTRIBUTE_COLUMN_PREFIX.length)
        : null;
}
/**
 * `columns`-parametrin jäsennys. Tyhjä tai puuttuva arvo tarkoittaa
 * oletusjoukkoa; tyhjä lista ei ole sallittu, koska sarakkeeton CSV on
 * pelkkä rivimäärä.
 */
export function parseExportColumns(value) {
    return value
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
}
/**
 * Vientiin päätyvät sarakkeet lopullisessa järjestyksessään.
 *
 * Kiinteät ensin kanonisessa järjestyksessä, attribuutit perässä
 * määrittelyjen järjestyksessä. Tuntematon tai passivoitu attribuutti
 * putoaa hiljaisesti pois: viennin osoite voi olla kirjanmerkissä, joka
 * on vanhempi kuin attribuutin poisto, eikä tallennetun linkin kuulu
 * lakata toimimasta siitä.
 */
export function resolveExportColumns(selected, definitions) {
    const wanted = new Set(selected ?? DEFAULT_EXPORT_COLUMNS);
    const columns = EXPORT_COLUMNS.filter((column) => wanted.has(column.id)).map((column) => column.id);
    // Kohde ei rajaa saraketta: asiakkaan attribuutti kelpaa riville
    // asiakkaan kautta, projektin projektin kautta ja periytyvä kummankin.
    const attributeKeys = definitions
        .filter((definition) => definition.active && wanted.has(attributeColumnId(definition.key)))
        .map((definition) => definition.key);
    return { columns: [...columns, ...attributeKeys.map(attributeColumnId)], attributeKeys };
}
//# sourceMappingURL=exportColumns.js.map